/**
 * rbush-backed spatial index for the Three.js renderer.
 *
 * # Why rbush
 *
 * Hit-test and viewport-cull both need fast bbox lookup over the full
 * entity set. R-trees are state-of-the-art for static / mostly-static
 * 2D AABB queries; `rbush` is a well-maintained pure-JS port that we
 * can populate via `bulkLoad` in a single O(N log N) pass — critical
 * for the 685k-entity П35Р fixture where re-inserting one at a time
 * would take seconds.
 *
 * # PR-A scope
 *
 * This file ships the wrapper API only. PR-B/C wire it into adapters
 * and the raycaster. The internal `Map<id, BBoxItem>` is what makes
 * `remove(id)` constant-time despite rbush itself only knowing how
 * to remove an item by reference equality.
 *
 * # Opt 3 — id-only storage
 *
 * Items in the rbush tree store **only the entity `id`** plus the
 * canonical `{minX, minY, maxX, maxY}` AABB rbush requires. We no
 * longer piggy-back the full `RenderableEntity` reference onto each
 * node — at 685k entities that pointer chain pinned ~100 MB of stub
 * objects in the JS heap. Callers that need the full entity supply
 * an `EntityResolver` at construction time; `intersect()` / `pick()`
 * return ids and callers walk the resolver themselves. The internal
 * bounds map (`itemsById`) is exposed via `getBounds(id)` so the
 * bounds-from-index helpers stay O(N) without round-tripping every
 * id through the resolver.
 */

import RBush from "rbush";

import type {
  RenderableEntity,
  SpatialIndex,
  Vec2,
  ViewportRect,
} from "./types";

/**
 * Resolver injected at construction. Returns the full RenderableEntity
 * for an id known to the index, or `undefined` when the id has been
 * dropped from the upstream renderable map between intersect/pick and
 * resolve. Callers MUST handle `undefined` defensively.
 *
 * CACHE-DIRECT (Codex PR #784 round-8 P2): on that path the resolver
 * `SceneBulkLoad.ts` installs is `renderableById` CHAINED with a metadata-stub
 * fallback (`state/spatialBuilder.ts createMetadataStubResolver`), so an id the
 * LOD pre-classify dropped as sub-pixel resolves to a SPATIAL STUB — real
 * `id` / `type` / `layer` / `bounds`, but a degenerate `point` geometry at the
 * bbox centre — instead of `undefined`. Without it `computeVisibleSet` dropped
 * those ids and the per-frame relift could never reach them (which is why whole
 * entity kinds had to be exempted from the drop). A caller that needs real
 * GEOMETRY must therefore detect the stub (`geometry.kind === "point"` with a
 * `type` other than `"Point"` — `state/metadataStub.ts
 * isMetadataStubRenderable`, the one shared predicate) and lift it
 * (`state/spatialBuilder.ts liftRenderableForId`, the seam
 * `scene/refinedEntityPick.ts` uses); a caller that needs only id / bounds /
 * layer / type can use the stub directly. The stub is transient: the first
 * relift of that id publishes the real renderable back into `renderableById`.
 */
export type EntityResolver = (id: string) => RenderableEntity | undefined;

/**
 * rbush stores arbitrary objects whose shape extends the canonical
 * `{ minX, minY, maxX, maxY }` AABB. Opt 3 — we store only the entity
 * `id` alongside the bbox; the full `RenderableEntity` is resolved
 * via the constructor-supplied `EntityResolver` when callers need it.
 */
interface BBoxItem {
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
  id: string;
}

/**
 * Phase 2 Wave 2 Task 5 — pending bbox for a dirty-add overlay item.
 * Identical shape to the rbush `BBoxItem` so a "dirty overlay" query
 * can intersect against the same coordinate system.
 */
interface DirtyBox {
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
}

/**
 * Phase 2 Wave 2 Task 5 — tunable thresholds for incremental rebuild.
 * Exposed for tests; production callers leave them at defaults.
 */
export interface SpatialIndexIncrementalConfig {
  /**
   * After this many dirty entries (added OR removed), the next query
   * is allowed to trigger a full rebuild instead of paying the overlay
   * walk on every search. Default 50 — matches the spec.
   */
  dirtyCountThreshold: number;
  /**
   * Minimum milliseconds between consecutive full rebuilds. Prevents
   * a churn of small mutations from re-running the O(N) rebuild back-
   * to-back. Default 100 ms — matches the spec.
   */
  minRebuildIntervalMs: number;
}

const DEFAULT_INCREMENTAL_CONFIG: SpatialIndexIncrementalConfig = {
  dirtyCountThreshold: 50,
  minRebuildIntervalMs: 100,
};

/**
 * Bounds resolver invoked during a dirty rebuild. The renderer wires
 * this to read each entity's current bbox from the compact cache. If
 * omitted, dirty additions can still overlay via `markDirtyAdded`'s
 * explicit bbox argument, but a full rebuild has no source to walk.
 */
export type DirtyBoundsResolver = (entityId: string) => DirtyBox | null;

/**
 * rbush ctor accepts a `nodeSize` argument; default 9 is balanced for
 * mixed read/write loads. We keep the default — viewport queries
 * dominate over insert/remove for pan/zoom workflows.
 */
export class RBushSpatialIndex implements SpatialIndex {
  private readonly tree: RBush<BBoxItem>;
  private readonly itemsById: Map<string, BBoxItem>;
  private resolveEntity: EntityResolver;
  // Phase 2 Wave 2 Task 5 — dirty-overlay state.
  private readonly dirtyAdded: Map<string, DirtyBox> = new Map();
  private readonly dirtyRemoved: Set<string> = new Set();
  /**
   * Wall-clock of the last full rebuild. `-Infinity` so the very first
   * threshold-driven check is permitted to rebuild without waiting on
   * `minRebuildIntervalMs`. Reset to `nowMs()` after each rebuild
   * (incl. `bulkLoad`).
   */
  private lastRebuildMs = -Infinity;
  private incrementalConfig: SpatialIndexIncrementalConfig =
    DEFAULT_INCREMENTAL_CONFIG;
  private dirtyBoundsResolver: DirtyBoundsResolver | null = null;
  // #589 — defaults to the common/legacy render path's convention (every
  // pre-#589 caller implicitly assumed offset-world bounds); explicitly
  // overwritten by SceneBulkLoad.ts's two `bulkLoad()` call sites.
  private boundsFrame: "offset-world" | "raw-world" = "offset-world";

  /**
   * `resolveEntity` is optional so existing tests / placeholder handles
   * that never call `pick()` / `intersect()` consumers don't have to
   * wire one up. When omitted the resolver returns `undefined` for
   * every id — equivalent to "no renderable available", and consumers
   * that need the full entity will see the same `undefined` they'd see
   * for a stale id.
   *
   * The resolver can be swapped at any time via `setEntityResolver` —
   * scene bulk-load passes build a fresh `renderableById` map per pass
   * and reattach it as the active resolver so hit-test / window-select
   * see the current pass's renderables without re-creating the index.
   */
  constructor(resolveEntity?: EntityResolver) {
    this.tree = new RBush<BBoxItem>();
    this.itemsById = new Map();
    this.resolveEntity = resolveEntity ?? (() => undefined);
  }

  /**
   * Replace the entity resolver. Called by `runSceneBulkLoad` after
   * each pass so subsequent `intersect()` / `pick()` consumers resolve
   * against the current pass's renderable map.
   */
  setEntityResolver(resolveEntity: EntityResolver): void {
    this.resolveEntity = resolveEntity;
  }

  insert(entity: RenderableEntity): void {
    // Replace-on-insert: if an entity with the same id already lives
    // in the tree (e.g. patchEntity path), drop the stale node first
    // so we don't accumulate duplicates. The rbush API requires us to
    // pass the *exact* old object reference back to `remove`.
    const stale = this.itemsById.get(entity.id);
    if (stale !== undefined) this.tree.remove(stale);

    const item: BBoxItem = {
      minX: entity.bounds.minX,
      minY: entity.bounds.minY,
      maxX: entity.bounds.maxX,
      maxY: entity.bounds.maxY,
      id: entity.id,
    };
    this.tree.insert(item);
    this.itemsById.set(entity.id, item);
  }

  remove(id: string): void {
    const item = this.itemsById.get(id);
    if (item === undefined) return;
    this.tree.remove(item);
    this.itemsById.delete(id);
  }

  intersect(rect: ViewportRect): string[] {
    // ViewportRect uses {left,top,right,bottom}; rbush wants
    // {minX,minY,maxX,maxY}. CAD Y-axis flip is handled by callers —
    // the index just stores whatever bbox numbers they hand us.
    this.maybeRebuildFromDirty();
    const items = this.tree.search({
      minX: rect.left,
      minY: rect.top,
      maxX: rect.right,
      maxY: rect.bottom,
    });
    return this.overlayDirty(rect, items);
  }

  /**
   * Coarse pick: returns the *first* entity whose bbox includes the
   * world point, expanded by the supplied tolerance. PR-C will refine
   * this to dispatch into per-kind analytic distance functions, but
   * the bbox prefilter remains the same.
   *
   * Returns the topmost entity id within tolerance, or `null` when no
   * candidate bbox is within range.
   *
   * F11 (codex P2 #12, post-cycle-1) — applies the dirty overlay to
   * the pick query so moves / removes since the last rebuild are
   * respected. Without this, a freshly-moved entity returned its old
   * bbox from the raw rbush tree until the next threshold-driven
   * rebuild — clicking where the entity used to be hit it, clicking
   * where it now lives missed it. Mirrors the contract `intersect()`
   * provides for window-select.
   */
  pick(world: Vec2, toleranceWorld: number): string | null {
    // F12 (codex P2, post-cycle-2) — mirror `intersect()` and compact
    // the dirty overlay via the threshold-driven rebuild. Without this,
    // a burst of moves/adds left every subsequent pick paying an O(dirty)
    // overlay walk (selection / hover) instead of a single O(log N)
    // rbush query, because `pick()` is the renderer's hot path between
    // viewport refreshes (which is where `intersect()` lives).
    this.maybeRebuildFromDirty();
    const queryRect: ViewportRect = {
      left: world.x - toleranceWorld,
      top: world.y - toleranceWorld,
      right: world.x + toleranceWorld,
      bottom: world.y + toleranceWorld,
    };
    const rawItems = this.tree.search({
      minX: queryRect.left,
      minY: queryRect.top,
      maxX: queryRect.right,
      maxY: queryRect.bottom,
    });
    // Apply dirty overlay: drop dirty-removed ids, prefer dirty-added
    // bboxes for ids present in both. Build the candidate list of
    // (id, bbox) pairs so the small-area tie-break still works with
    // the overlay-aware data.
    const candidates: BBoxItem[] = [];
    const seen = new Set<string>();
    for (const item of rawItems) {
      if (this.dirtyRemoved.has(item.id)) continue;
      const dirtyBounds = this.dirtyAdded.get(item.id);
      if (dirtyBounds !== undefined) {
        if (!bbOverlaps(queryRect, dirtyBounds)) continue;
        candidates.push({ ...dirtyBounds, id: item.id });
        seen.add(item.id);
        continue;
      }
      candidates.push(item);
      seen.add(item.id);
    }
    // Append dirty-added entries not already represented above (= ids
    // brand-new since the last rebuild).
    if (this.dirtyAdded.size > 0) {
      for (const [id, bounds] of this.dirtyAdded) {
        if (this.dirtyRemoved.has(id)) continue;
        if (seen.has(id)) continue;
        if (bbOverlaps(queryRect, bounds)) {
          candidates.push({ ...bounds, id });
          seen.add(id);
        }
      }
    }
    if (candidates.length === 0) return null;
    // Stable: pick the smallest-area bbox so denser geometry wins. The
    // analytic refinement in PR-C will replace this with a true
    // distance test; for now this prevents large background hatches
    // from eating clicks meant for narrow lines on top of them.
    let best = candidates[0]!;
    let bestArea = bboxArea(best);
    for (let i = 1; i < candidates.length; i++) {
      const candidate = candidates[i]!;
      const area = bboxArea(candidate);
      if (area < bestArea) {
        best = candidate;
        bestArea = area;
      }
    }
    return best.id;
  }

  bulkLoad(entities: RenderableEntity[]): void {
    const items: BBoxItem[] = entities.map((entity) => ({
      minX: entity.bounds.minX,
      minY: entity.bounds.minY,
      maxX: entity.bounds.maxX,
      maxY: entity.bounds.maxY,
      id: entity.id,
    }));
    this.tree.clear();
    this.itemsById.clear();
    if (items.length > 0) this.tree.load(items);
    for (const item of items) this.itemsById.set(item.id, item);
    // Phase 2 Wave 2 Task 5 — bulkLoad is the canonical rebuild; reset
    // dirty state so the freshly loaded tree is the source of truth.
    this.dirtyAdded.clear();
    this.dirtyRemoved.clear();
    this.lastRebuildMs = nowMs();
  }

  size(): number {
    return this.itemsById.size;
  }

  clear(): void {
    this.tree.clear();
    this.itemsById.clear();
    this.dirtyAdded.clear();
    this.dirtyRemoved.clear();
    this.lastRebuildMs = -Infinity;
    this.boundsFrame = "offset-world";
  }

  /** See `SpatialIndex.getBoundsFrame`'s doc for the contract. */
  getBoundsFrame(): "offset-world" | "raw-world" {
    return this.boundsFrame;
  }

  /**
   * Called by `SceneBulkLoad.ts` right after each `bulkLoad()` — the
   * frame ALWAYS changes together with a full bulk-load rebuild (this
   * class has no incremental/patch population path for a mismatched
   * frame to survive across, see `dep-graph-refactor` audit in the
   * #589 PR discussion for why).
   */
  setBoundsFrame(frame: "offset-world" | "raw-world"): void {
    this.boundsFrame = frame;
  }

  /* ── Phase 2 Wave 2 Task 5 — incremental dirty API ─────────────── */

  /**
   * Mark an entity as dirty-added. The bbox overlay is consulted on
   * the next `intersect()` until either a full rebuild happens (via
   * `bulkLoad` or the threshold-driven `maybeRebuildFromDirty`) or the
   * entity is explicitly dirty-removed.
   *
   * If `bounds` is provided, it overrides whatever the dirty-bounds
   * resolver would return for this id; useful when the renderer
   * already has the fresh bbox in hand from a recent computeBounds.
   */
  markDirtyAdded(entityId: string, bounds: DirtyBox): void {
    this.dirtyAdded.set(entityId, { ...bounds });
    // Cancel a pending dirty-remove if any — the entity is back.
    this.dirtyRemoved.delete(entityId);
  }

  /**
   * Mark an entity for dirty removal. The overlay will filter it out
   * of subsequent search results even if it's still in the main tree.
   */
  markDirtyRemoved(entityId: string): void {
    this.dirtyRemoved.add(entityId);
    this.dirtyAdded.delete(entityId);
  }

  /**
   * Inspect current dirty-state size — used by callers that want to
   * trigger an early rebuild after a known burst (e.g. after a multi-
   * entity commit).
   */
  dirtyStats(): { added: number; removed: number; lastRebuildMs: number } {
    return {
      added: this.dirtyAdded.size,
      removed: this.dirtyRemoved.size,
      lastRebuildMs: this.lastRebuildMs,
    };
  }

  /**
   * Configure incremental thresholds. Used by tests to inject tight
   * limits; production stays on defaults.
   */
  setIncrementalConfig(config: Partial<SpatialIndexIncrementalConfig>): void {
    this.incrementalConfig = {
      ...this.incrementalConfig,
      ...config,
    };
  }

  /**
   * Wire a bounds resolver so threshold-driven rebuilds can refresh
   * every entry from a canonical source (compact cache / metadata
   * index). Without a resolver, threshold rebuilds operate on the
   * existing `itemsById` snapshot plus the dirty overlay.
   */
  setDirtyBoundsResolver(resolver: DirtyBoundsResolver | null): void {
    this.dirtyBoundsResolver = resolver;
  }

  /**
   * Force a rebuild now, regardless of the time / count thresholds.
   * Reads the current `itemsById` plus the dirty overlay; if a bounds
   * resolver is wired, it takes priority over the cached bbox.
   */
  rebuildFromDirty(): void {
    const fresh: BBoxItem[] = [];
    // Walk the existing canonical set, minus dirty-removed ids.
    //
    // F7 (codex P2 #8, post-cycle-1) — precedence order for the bbox
    // each id contributes to the rebuilt tree:
    //   1. `dirtyAdded[id]` — the freshest authoritative bbox the bus
    //      has handed us. Takes priority over both the resolver and
    //      the cached `itemsById` bbox.
    //   2. `dirtyBoundsResolver(id)` — the wired snapshot source
    //      (compact cache / metadata index).
    //   3. `itemsById[id]` — the previous tree's cached bbox.
    //
    // Before the fix, dirty-added ids that already lived in `itemsById`
    // were re-emitted from the `itemsById` walk with their STALE bbox
    // because the dirty-added pass at the bottom only added ids that
    // were NOT in `itemsById`. Net effect: a threshold-driven rebuild
    // dropped every dirty-added bbox on the floor for any entity that
    // also had a previous bbox in the tree — the very entities most
    // likely to have moved.
    for (const [id, item] of this.itemsById) {
      if (this.dirtyRemoved.has(id)) continue;
      const dirty = this.dirtyAdded.get(id);
      if (dirty) {
        fresh.push({ ...dirty, id });
        continue;
      }
      const bounds =
        this.dirtyBoundsResolver !== null
          ? this.dirtyBoundsResolver(id)
          : null;
      if (bounds) {
        fresh.push({ ...bounds, id });
      } else {
        fresh.push({ ...item });
      }
    }
    // Add dirty-added entries that were NOT already represented above
    // (= ids brand-new since the last rebuild).
    for (const [id, bounds] of this.dirtyAdded) {
      if (this.dirtyRemoved.has(id)) continue;
      if (this.itemsById.has(id)) {
        // Already emitted in the itemsById walk above with the dirty
        // bbox (F7 precedence) — skip the duplicate.
        continue;
      }
      fresh.push({ ...bounds, id });
    }
    this.tree.clear();
    this.itemsById.clear();
    if (fresh.length > 0) this.tree.load(fresh);
    for (const item of fresh) this.itemsById.set(item.id, item);
    this.dirtyAdded.clear();
    this.dirtyRemoved.clear();
    this.lastRebuildMs = nowMs();
  }

  /**
   * Trigger a full rebuild if the dirty state has crossed both
   * thresholds (count + min interval). Called automatically from
   * `intersect()`; tests can also invoke directly.
   */
  private maybeRebuildFromDirty(): void {
    const totalDirty = this.dirtyAdded.size + this.dirtyRemoved.size;
    if (totalDirty < this.incrementalConfig.dirtyCountThreshold) return;
    const now = nowMs();
    if (now - this.lastRebuildMs < this.incrementalConfig.minRebuildIntervalMs)
      return;
    this.rebuildFromDirty();
  }

  /**
   * Apply the dirty overlay to a set of search results. Filters out
   * dirty-removed ids, then appends dirty-added entries whose bbox
   * overlaps the query rect.
   *
   * F6 (codex P2) — when an id appears in BOTH the main tree (old
   * bbox) AND `dirtyAdded` (new bbox), the new bbox is the authority.
   * Previously the main-tree match was always kept, which produced
   * stale hits for entities whose geometry moved outside the query
   * rect (the old bbox still intersected the query, the new one does
   * not). Now we revalidate every main-tree match that has a dirty-
   * added override against its NEW bbox and drop it when the new bbox
   * no longer overlaps the query.
   */
  private overlayDirty(rect: ViewportRect, items: BBoxItem[]): string[] {
    const out: string[] = [];
    const seen = new Set<string>();
    for (const item of items) {
      if (this.dirtyRemoved.has(item.id)) continue;
      const dirtyBounds = this.dirtyAdded.get(item.id);
      if (dirtyBounds !== undefined) {
        // The old tree match used the stale bbox. Revalidate against
        // the dirty-added bbox (which is the authoritative new value)
        // and drop the id if the new bbox no longer overlaps the rect.
        if (!bbOverlaps(rect, dirtyBounds)) continue;
      }
      out.push(item.id);
      seen.add(item.id);
    }
    if (this.dirtyAdded.size > 0) {
      for (const [id, bounds] of this.dirtyAdded) {
        if (this.dirtyRemoved.has(id)) continue;
        if (seen.has(id)) continue;
        if (bbOverlaps(rect, bounds)) {
          out.push(id);
          seen.add(id);
        }
      }
    }
    return out;
  }

  /**
   * O(1) bbox lookup for an id known to the index. Returns `null`
   * when the id has been dropped (or was never inserted). Used by
   * `scenePlaceholder.computeBoundsFromSpatialIndex*` so the bounds-
   * aggregate helpers don't have to round-trip through the resolver
   * — bounds are intrinsic to the index, the full entity is not.
   */
  getBounds(
    id: string,
  ): { minX: number; minY: number; maxX: number; maxY: number } | null {
    const item = this.itemsById.get(id);
    if (!item) return null;
    return {
      minX: item.minX,
      minY: item.minY,
      maxX: item.maxX,
      maxY: item.maxY,
    };
  }

  /**
   * Iterate every id currently tracked. O(N). Used by helpers that
   * need to walk the whole set without a viewport query (e.g.
   * `computeBoundsFromSpatialIndex` for fit-to-extents).
   */
  ids(): IterableIterator<string> {
    return this.itemsById.keys();
  }

  /**
   * Resolve an id to a full RenderableEntity via the constructor-
   * supplied resolver. Returns `undefined` when no resolver was set
   * or the resolver itself returned `undefined`.
   */
  resolve(id: string): RenderableEntity | undefined {
    return this.resolveEntity(id);
  }
}

function bboxArea(item: BBoxItem): number {
  const w = Math.max(0, item.maxX - item.minX);
  const h = Math.max(0, item.maxY - item.minY);
  return w * h;
}

/**
 * Phase 2 Wave 2 Task 5 — millisecond timestamp source. Reads
 * `performance.now()` when available (browser, jsdom) and falls back
 * to `Date.now()` for Node-only contexts. The threshold-driven rebuild
 * doesn't care about the wall-clock epoch, only relative deltas.
 */
function nowMs(): number {
  if (
    typeof performance !== "undefined" &&
    typeof performance.now === "function"
  ) {
    return performance.now();
  }
  return Date.now();
}

/**
 * Phase 2 Wave 2 Task 5 — bbox overlap predicate. Inclusive on edges
 * (matches rbush's default behaviour). Used to filter dirty-added
 * entries against the query rect.
 */
function bbOverlaps(
  rect: { left: number; top: number; right: number; bottom: number },
  box: { minX: number; minY: number; maxX: number; maxY: number },
): boolean {
  if (rect.right < box.minX || rect.left > box.maxX) return false;
  if (rect.bottom < box.minY || rect.top > box.maxY) return false;
  return true;
}

/** Convenience factory matching the pattern used by other modules. */
export function createSpatialIndex(resolveEntity?: EntityResolver): SpatialIndex {
  return new RBushSpatialIndex(resolveEntity);
}
